import { useState, useRef, useEffect } from "react";
import { useMediaQuery } from "react-responsive";
import { EditQsModal } from "./EditQsModal";
import { client } from "helper/apolloConfig";
import { updateSummaryCount, updateSummaryCount1, updateTrialzEarnedDate, addManualQ, removeManualQ } from "redux/features/dogSlice/dogGql";
import Modal from "shared/Components/modal";

const EditeQs = ({ title, data, isAdmin, isCountUpdatedHandler, dogId }) => {
    const [loadingAction, setLoadingAction] = useState({ id: null, type: null });
    const modalRef = useRef();
    const titleDateModalRef = useRef();
    const [apiData, setApiData] = useState({
        title: "",
        loading: false,
        type: "pending",
    });
    const [showTitleDateModal, setShowTitleDateModal] = useState(false);
    const [titleDate, setTitleDate] = useState("");
    const [pendingUpdate, setPendingUpdate] = useState(null);
    const [showQDateModal, setShowQDateModal] = useState(false);
    const [qDate, setQDate] = useState("");
    const [pendingQAdd, setPendingQAdd] = useState(null);
    const qDateModalRef = useRef();
    const isMobile = useMediaQuery({ query: "(max-width: 480px)" });
    const isTablet = useMediaQuery({
        query: "(max-width: 1024px) and (min-width: 480px)",
    });

    // Open the title date modal when showTitleDateModal becomes true
    useEffect(() => {
        if (showTitleDateModal && titleDateModalRef.current) {
            titleDateModalRef.current.openModal();
        }
    }, [showTitleDateModal]);

    // Open the Q date modal when showQDateModal becomes true
    useEffect(() => {
        if (showQDateModal && qDateModalRef.current) {
            qDateModalRef.current.openModal();
        }
    }, [showQDateModal]);

    const handleApply = async (id, search, count, type) => {
        setLoadingAction({ id, type });
        const summaryType = title.includes("Pro") ? "P" : "S";

        // When ADDING a Q, always ask for the date first
        if (type === "add") {
            setPendingQAdd({ id, search, count, summaryType });
            setShowQDateModal(true);
            setLoadingAction({ id: null, type: null });
            return;
        }

        // When REMOVING a Q, use removeManualQ mutation
        try {
            const result = await client.mutate({
                mutation: removeManualQ,
                variables: {
                    dogId: parseInt(dogId),
                    search: search,
                    summaryType: summaryType,
                }
            });

            const response = result.data.removeManualQ;

            if (response.success) {
                if (response.milestoneRemoved) {
                    setApiData({
                        title: `Q removed! Title ${response.removedTitleShortName} was also removed. Current count: ${response.currentCount}`,
                        type: "final",
                    });
                } else {
                    setApiData({
                        title: `Q removed successfully! Current count: ${response.currentCount}`,
                        type: "final",
                    });
                }
                modalRef.current.openModal();
                isCountUpdatedHandler();
            }
        } catch (error) {
            setApiData({
                title: `Failed to remove Q: ${error.message}`,
                type: "error",
            });
            modalRef.current.openModal();
        } finally {
            setLoadingAction({ id: null, type: null });
        }
    };

    // Helper function to generate shortName
    const generateShortName = (search, div, level) => {
        const words = search.split(' ');
        let meaningfulWord = words[words.length - 1];
        if (!isNaN(Number(meaningfulWord))) {
            meaningfulWord = words[words.length - 2] || '';
        }
        const lastWordInitial = meaningfulWord[0]?.toUpperCase() || '';

        let baseShortName = '';
        if (search.includes('Premier')) {
            baseShortName = `SGP-${lastWordInitial}`;
        } else if (search.includes('Excellent')) {
            baseShortName = `SGE-${lastWordInitial}`;
        } else if (search.includes('Advanced')) {
            baseShortName = `SGA-${lastWordInitial}`;
        } else if (search.includes('Novice')) {
            baseShortName = `SGN-${lastWordInitial}`;
        }

        if (div === 'P' && level) {
            const match = baseShortName.match(/(SG[PEAN])-(.)/)
            if (match) {
                return `${match[1]}-${match[2]}${level}`;
            }
        }

        return baseShortName;
    };

    // Handle Q date submission (when adding a Q)
    const handleQDateSubmit = async () => {
        if (!qDate) {
            alert("Please enter a date for this qualifying score");
            return;
        }

        try {
            // Call the new addManualQ mutation
            const result = await client.mutate({
                mutation: addManualQ,
                variables: {
                    dogId: parseInt(dogId),
                    search: pendingQAdd.search,
                    summaryType: pendingQAdd.summaryType,
                    date: qDate,
                }
            });

            const response = result.data.addManualQ;

            // Close Q date modal
            qDateModalRef.current?.closeModal();
            setShowQDateModal(false);
            setQDate("");

            // Check if milestone was reached
            if (response.milestoneReached) {
                // Calculate milestone details
                const currentCount = response.currentCount;
                const summaryType = pendingQAdd.summaryType;
                let milestoneShortName = "";

                if (summaryType === 'S') {
                    // Standard milestone at 3
                    milestoneShortName = generateShortName(pendingQAdd.search, 'S');
                } else if (summaryType === 'P') {
                    // Pro milestones at 5, 10, 15, 20...
                    const level = (currentCount / 5) + 1;
                    milestoneShortName = generateShortName(pendingQAdd.search, 'P', level);
                }

                // Show the EXISTING title date modal with the Q date pre-filled
                setPendingUpdate({
                    id: pendingQAdd.id,
                    search: pendingQAdd.search,
                    count: currentCount - 1, // Old count
                    newCount: currentCount, // New count
                    type: 'add',
                    summaryType: pendingQAdd.summaryType,
                    milestoneShortName: milestoneShortName,
                    fromManualQ: true // Flag to indicate count is already updated
                });
                setTitleDate(qDate); // Pre-fill with the Q date
                setShowTitleDateModal(true);
                setPendingQAdd(null);
            } else {
                // No milestone - just show success
                setPendingQAdd(null);
                setApiData({
                    title: `Q added successfully! Current count: ${response.currentCount}`,
                    type: "final",
                });
                modalRef.current.openModal();
                isCountUpdatedHandler();
            }

        } catch (error) {
            qDateModalRef.current?.closeModal();
            setShowQDateModal(false);
            setQDate("");
            setPendingQAdd(null);

            setApiData({
                title: `Failed to add Q: ${error.message}`,
                type: "error",
            });
            modalRef.current.openModal();
        }
    };

    // Handle title date submission
    const handleTitleDateSubmit = async () => {
        if (!titleDate) {
            alert("Please enter a date for the title");
            return;
        }

        try {
            // Create TitleEarnedDate
            await client.mutate({
                mutation: updateTrialzEarnedDate,
                variables: {
                    dogId: parseInt(dogId),
                    search: pendingUpdate.search,
                    shortName: pendingUpdate.milestoneShortName,
                    date: titleDate,
                    summaryType: pendingUpdate.summaryType
                }
            });

            // Only update the count if NOT from manual Q (manual Q already updated it)
            if (!pendingUpdate.fromManualQ) {
                const isIdNumber = !isNaN(pendingUpdate.id);
                const variables1 = {
                    id: pendingUpdate.id,
                    count: pendingUpdate.newCount,
                };
                const variables2 = {
                    id: pendingUpdate.id,
                    dogId: +dogId,
                    search: pendingUpdate.id,
                    summaryType: pendingUpdate.summaryType,
                    count: pendingUpdate.newCount,
                };

                await client.mutate({
                    mutation: isIdNumber ? updateSummaryCount : updateSummaryCount1,
                    variables: isIdNumber ? variables1 : variables2,
                });
            }

            // Success!
            titleDateModalRef.current?.closeModal();
            setShowTitleDateModal(false);
            setTitleDate("");
            setPendingUpdate(null);

            setApiData({
                title: `Title ${pendingUpdate.milestoneShortName} created successfully!`,
                type: "final",
            });
            modalRef.current.openModal();

            isCountUpdatedHandler();

        } catch (error) {
            titleDateModalRef.current?.closeModal();
            setShowTitleDateModal(false);
            setTitleDate("");
            setPendingUpdate(null);

            setApiData({
                title: `Failed to create title: ${error.message}`,
                type: "final",
            });
            modalRef.current.openModal();
        }
    };
    const onCloseModal = () => {
        modalRef.current.closeModal();
    };
    return (
        <>
            <EditQsModal
                modalRef={modalRef}
                error={apiData.type}
                apiData={apiData}
                onCloseModal={onCloseModal}
            />
            <div className="my-5 md:my-0">
                <div
                    className={`shadow-100 py-0 border rounded-lg overflow-hidden ${
                        isTablet ? "w-[100%] min-h-[260px]" : ""
                    }`}
                >
                    <div className="bg-lightBlue/60 py-4 px-2 flex items-center h-[17%]">
                        <span className="px-4 text-xl font-semibold text-gray-900">{title}</span>
                    </div>
                    <div className="h-[83%] overflow-y-auto py-4 px-4 w-full">
                        {data?.length ? (
                            data.map((item) => (
                                <div
                                    key={item.key}
                                    className={`flex-row flex justify-between items-center ${
                                        isMobile ? "border-b border-gray-200 py-4" : "mb-4 last:mb-0"
                                    }`}
                                >
                                    <span className="font-medium text-base text-gray-700">
                                        {item.search}
                                    </span>
                                    {isAdmin ? (
                                        <div className="flex items-center">
                                            <button
                                                disabled={
                                                    loadingAction.id === item.id &&
                                                    loadingAction.type === "remove"
                                                }
                                                onClick={() => {
                                                    if (item.count <= 0) {
                                                        setApiData({
                                                            title: `Count cannot be less than 0 for ${item.search}.`,
                                                            type: "error",
                                                        });
                                                        modalRef.current.openModal();
                                                        return;
                                                    }
                                                    handleApply(item.id, item.search, item.count, "remove");
                                                }}
                                                className={`w-[45px] bg-lightBlue/60 py-2 px-4 font-bold border rounded-md
                                                    ${
                                                        loadingAction.id === item.id &&
                                                        loadingAction.type === "remove"
                                                            ? "cursor-wait opacity-50"
                                                            : "cursor-pointer"
                                                    }
                                                `}
                                            >
                                                -
                                            </button>
                                            <span className="mx-4 text-base font-medium text-gray-700">
                                                {item.count}
                                            </span>
                                            <button
                                                disabled={
                                                    loadingAction.id === item.id &&
                                                    loadingAction.type === "add"
                                                }
                                                onClick={() =>
                                                    handleApply(item.id, item.search, item.count, "add")
                                                }
                                                className={`w-[45px] bg-lightBlue/60 py-2 px-4 font-bold border rounded-md
                                                    ${
                                                        loadingAction.id === item.id &&
                                                        loadingAction.type === "add"
                                                            ? "cursor-wait opacity-50"
                                                            : "cursor-pointer"
                                                    }
                                                `}
                                            >
                                                +
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="mx-4 text-base font-medium text-gray-700">
                                            {item.count}
                                        </span>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="flex flex-col justify-center items-center min-h-[290px]">
                                <span className="text-base font-medium text-gray-700">
                                    No records available.
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Q Date Modal - Asks for date when adding a Q */}
            {showQDateModal && pendingQAdd && (
                <Modal ref={qDateModalRef}>
                    <div className="bg-white p-6 rounded-lg max-w-md w-full">
                        <h2 className="text-2xl font-bold mb-4 text-gray-900">
                            Add Qualifying Score
                        </h2>

                        <div className="mb-4">
                            <p className="mb-3 text-gray-700">
                                You are adding a Q to:
                            </p>
                            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                                <p className="text-lg font-semibold text-blue-900">
                                    {pendingQAdd.search}
                                </p>
                                <p className="text-sm text-blue-700">
                                    {pendingQAdd.summaryType === 'S' ? 'Standard' : 'Pro'} Division
                                </p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block font-semibold mb-2 text-gray-900">
                                When was this Q earned?
                            </label>
                            <input
                                type="date"
                                value={qDate}
                                onChange={(e) => setQDate(e.target.value)}
                                className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-sm text-gray-600 mt-2">
                                Enter the date when this qualifying score was earned
                            </p>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    qDateModalRef.current?.closeModal();
                                    setShowQDateModal(false);
                                    setPendingQAdd(null);
                                    setQDate("");
                                    setLoadingAction({ id: null, type: null });
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 font-medium bg-white"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleQDateSubmit}
                                className="px-4 py-2 !bg-blue-600 !text-white rounded-md hover:!bg-blue-700 font-medium"
                                style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
                            >
                                Add Q
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Title Date Modal */}
            {showTitleDateModal && pendingUpdate && (
                <Modal ref={titleDateModalRef}>
                    <div className="bg-white p-6 rounded-lg max-w-md w-full">
                        <h2 className="text-2xl font-bold mb-4 text-gray-900">
                            🎉 Title Earned!
                        </h2>

                        <div className="mb-4">
                            <p className="mb-3 text-gray-700">
                                Congratulations! This dog has earned:
                            </p>
                            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                                <p className="text-lg font-semibold text-blue-900">
                                    {pendingUpdate.milestoneShortName}
                                </p>
                                <p className="text-sm text-blue-700">
                                    {pendingUpdate.search} ({pendingUpdate.summaryType === 'S' ? 'Standard' : 'Pro'})
                                </p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block font-semibold mb-2 text-gray-900">
                                When was this title earned?
                            </label>
                            <input
                                type="date"
                                value={titleDate}
                                onChange={(e) => setTitleDate(e.target.value)}
                                className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-sm text-gray-600 mt-2">
                                Enter the date when this title was earned
                            </p>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    titleDateModalRef.current?.closeModal();
                                    setShowTitleDateModal(false);
                                    setPendingUpdate(null);
                                    setTitleDate("");
                                    setLoadingAction({ id: null, type: null });
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 font-medium bg-white"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleTitleDateSubmit}
                                className="px-4 py-2 !bg-blue-600 !text-white rounded-md hover:!bg-blue-700 font-medium"
                                style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
                            >
                                Create Title
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default EditeQs;
